import { computed, toValue } from 'vue'
import type { ComputedRef, MaybeRefOrGetter } from 'vue'

import useI18n from '@app/shared/vue/composables/use-I18n'
import type { PeriodSelectionOption } from '../types'

export interface PeriodSelectionTriggerProps {
  periodSelectionOption: MaybeRefOrGetter<PeriodSelectionOption | undefined>
  isParentSelected: MaybeRefOrGetter<boolean>
  // The resolved date/lock-type text: the same value shown inside the
  // selected row's button, reused as the plain-text row's interpolated value.
  value: MaybeRefOrGetter<string>
}

/**
 * Shared by the period-selection dropdowns and the selected-option explainer:
 * the prefix/suffix text around the selected row's button-based trigger, the
 * combined plain-text title built from that same prefix/suffix for the
 * unselected rendering, and the bold-vs-normal weight shared by both the
 * selected and unselected renderings.
 *
 * Each field accepts a ref, a getter, or a plain value (MaybeRefOrGetter):
 * PeriodSelectionTrigger passes toRefs(props) through so its reads stay live,
 * period-selection-info.vue passes its own computeds directly, with no
 * getter-object adapter in between.
 */
export function usePeriodSelectionTrigger(props: PeriodSelectionTriggerProps): {
  titlePrefix: ComputedRef<string>
  titleSuffix: ComputedRef<string>
  title: ComputedRef<string>
  triggerClass: ComputedRef<string>
} {
  const I18n = useI18n()

  const titlePrefix = computed(() => {
    const option = toValue(props.periodSelectionOption)
    return option ? I18n.t(`${option.titleKey}_prefix`) : ''
  })

  // Empty for most option/locale pairs, but not a dead concept: Dutch's
  // completed_period_title_suffix is "werd", since Dutch word order puts
  // that verb after the interpolated value. defaultValue covers the common
  // case without forcing every locale to declare an empty key.
  const titleSuffix = computed(() => {
    const option = toValue(props.periodSelectionOption)
    return option
      ? I18n.t(`${option.titleKey}_suffix`, { defaultValue: '' })
      : ''
  })

  // period-selection-info.vue styles .dropdown-trigger to make the value bold
  // in the sidebar. The unselected modal rows have no rule for it.
  const title = computed(() =>
    [
      titlePrefix.value,
      `<span class="dropdown-trigger">${toValue(props.value)}</span>`,
      titleSuffix.value,
    ]
      .filter(Boolean)
      .join(' ')
  )

  const triggerClass = computed(() =>
    toValue(props.isParentSelected) ? 'tw-font-medium' : 'tw-font-normal'
  )

  return { titlePrefix, titleSuffix, title, triggerClass }
}
